import React, { useState } from "react";
import { Sliders, Type, Columns, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
export type { ReaderPrefs } from "./readerPreferences";
export { DEFAULT_READER_PREFS } from "./readerPreferences";
import type { ReaderPrefs } from "./readerPreferences";
import { DEFAULT_READER_PREFS } from "./readerPreferences";

interface ReaderPreferencesModalProps {
  prefs: ReaderPrefs;
  onChange: (prefs: ReaderPrefs) => void;
  trigger?: React.ReactNode;
}

export function ReaderPreferencesModal({
  prefs,
  onChange,
  trigger,
}: ReaderPreferencesModalProps) {
  const [open, setOpen] = useState(false);

  const update = (key: keyof ReaderPrefs, val: string) => {
    const next = { ...prefs, [key]: val };
    onChange(next);
  };

  const handleReset = () => {
    onChange(DEFAULT_READER_PREFS);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button
            className="h-10 px-3.5 rounded-full border border-border/60 bg-card hover:bg-accent text-xs font-semibold text-foreground inline-flex items-center gap-1.5 transition-colors shadow-sm"
            aria-label="Customize reader preferences"
          >
            <Sliders className="h-3.5 w-3.5 text-primary" />
            <span>Reader Mode</span>
          </button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-xl border-border/60 shadow-2xl p-6">
        <DialogHeader className="pb-4 border-b border-border/40">
          <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
            <Sliders className="h-5 w-5 text-primary" />
            Reader Preferences
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Customize typography and layout for your optimal reading flow.
          </p>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Font Family */}
          <div className="space-y-2.5">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Type className="h-3.5 w-3.5" /> Typeface
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "sans", label: "Modern Sans", sample: "Aa Sans" },
                { id: "serif", label: "Editorial Serif", sample: "Aa Serif" },
                { id: "mono", label: "Technical Mono", sample: "Aa Mono" },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => update("fontFamily", f.id)}
                  className={cn(
                    "p-3 rounded-xl border text-center transition-all",
                    prefs.fontFamily === f.id
                      ? "border-primary bg-primary/10 text-primary shadow-sm font-bold"
                      : "border-border/60 hover:bg-accent text-muted-foreground",
                  )}
                >
                  <span className="block text-sm leading-tight mb-1">{f.sample}</span>
                  <span className="block text-[11px] opacity-80">{f.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Font Size */}
          <div className="space-y-2.5">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Font Size
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { id: "sm", label: "Compact", size: "15px" },
                { id: "base", label: "Default", size: "17px" },
                { id: "lg", label: "Large", size: "19px" },
                { id: "xl", label: "Expanded", size: "22px" },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => update("fontSize", s.id)}
                  className={cn(
                    "py-2.5 px-2 rounded-xl border text-center transition-all",
                    prefs.fontSize === s.id
                      ? "border-primary bg-primary/10 text-primary shadow-sm font-bold"
                      : "border-border/60 hover:bg-accent text-muted-foreground",
                  )}
                >
                  <span className="block text-xs font-medium leading-none mb-1">{s.label}</span>
                  <span className="block text-[10px] opacity-70">{s.size}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Column Width */}
          <div className="space-y-2.5">
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Columns className="h-3.5 w-3.5" /> Reading Column Width
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "prose", label: "Optimal (68ch)", desc: "Fatigue-free" },
                { id: "wide", label: "Spacious", desc: "Wider rail" },
                { id: "full", label: "Full Container", desc: "Edge to edge" },
              ].map((w) => (
                <button
                  key={w.id}
                  onClick={() => update("columnWidth", w.id)}
                  className={cn(
                    "p-2.5 rounded-xl border text-center transition-all",
                    prefs.columnWidth === w.id
                      ? "border-primary bg-primary/10 text-primary shadow-sm font-bold"
                      : "border-border/60 hover:bg-accent text-muted-foreground",
                  )}
                >
                  <span className="block text-xs font-semibold leading-tight mb-0.5">{w.label}</span>
                  <span className="block text-[10px] opacity-70">{w.desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-border/40 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
          </Button>

          <Button
            size="sm"
            onClick={() => setOpen(false)}
            className="rounded-full px-5 text-xs font-semibold"
          >
            Apply & Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
